import React, { useState } from "react";
import ReactDOM from "react-dom";
import { ChevronLeft } from "lucide-react";
import ActivityStep from "./steps/ActivityStep";
import PlaceStep, { DatePlace } from "./steps/PlaceStep";
import WhenStep from "./steps/WhenStep";
import RecapStep from "./steps/RecapStep";
import type { SendInvitationPayload } from "../../hooks/useDateState";

const TOTAL_STEPS = 4;

export default function DateOrganizerSheet({
  lang, myLat, myLng, onSend, onClose,
}: {
  lang: string; myLat?: number | null; myLng?: number | null;
  onSend: (payload: SendInvitationPayload) => Promise<any>;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [activityKey, setActivityKey] = useState<string | null>(null);
  const [place, setPlace] = useState<DatePlace | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [priceNote, setPriceNote] = useState("");
  const [sending, setSending] = useState(false);

  const canContinue = step === 1 ? !!activityKey : step === 2 ? !!place?.name : step === 3 ? !!(date && time) : true;

  const handleSend = async () => {
    if (!activityKey || !place || !date || !time) return;
    setSending(true);
    try {
      const data = await onSend({
        activity_key: activityKey,
        place: { name: place.name, address: place.address, lat: place.lat, lng: place.lng, venue_id: place.venue_id },
        date, time,
        price_note: priceNote.trim() || undefined,
      });
      if (data?.invitation) onClose();
    } finally {
      setSending(false);
    }
  };

  // Portail direct sur document.body — sur mobile, ce sheet est ouvert depuis ProfileVisitor.tsx,
  // lui-même rendu dans le conteneur scrollable de Layout.tsx (WebkitOverflowScrolling:"touch").
  // Sur Safari iOS, un position:fixed descendant d'un tel ancêtre se positionne par rapport à TOUT
  // son contenu scrollable plutôt qu'au vrai viewport — le sheet se retrouvait très bas, hors
  // écran, bouton "suivant" y compris, exactement le bug déjà rencontré et corrigé sur
  // PhotoLightbox.tsx. Le portail échappe à cet ancêtre.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          {step > 1 ? (
            <button onClick={() => setStep(s => s - 1)} style={{ border: "none", background: "none", cursor: "pointer", color: "#666", padding: 0, display: "flex" }}>
              <ChevronLeft size={22} />
            </button>
          ) : <span />}
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, backgroundColor: i < step ? "#0a0a0a" : "#eee" }} />
          ))}
        </div>

        {step === 1 && <ActivityStep lang={lang} value={activityKey} onSelect={key => { setActivityKey(key); setTimeout(() => setStep(2), 120); }} />}
        {step === 2 && <PlaceStep lang={lang} value={place} onChange={setPlace} myLat={myLat} myLng={myLng} />}
        {step === 3 && (
          <WhenStep lang={lang} date={date} time={time} priceNote={priceNote}
            onDateChange={setDate} onTimeChange={setTime} onPriceNoteChange={setPriceNote} />
        )}
        {step === 4 && (
          <RecapStep lang={lang} activityKey={activityKey} place={place} date={date} time={time}
            onSend={handleSend} onEdit={() => setStep(1)} sending={sending} />
        )}

        {step < 4 && (
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canContinue}
            style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 14, border: "none", backgroundColor: canContinue ? "#0a0a0a" : "#eee", color: canContinue ? "white" : "#aaa", fontSize: 14, fontWeight: 700, cursor: canContinue ? "pointer" : "default" }}
          >
            →
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
